import { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { User, Mail, Phone, Shield, Copy, Check, MapPin, Plus, Trash2, Home, Pencil, X } from "lucide-react";
import axios from "axios";
import { geocodeAddress } from "../services/mapService";
import AddressFormGroup from "../components/AddressFormGroup";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const [newAddressData, setNewAddressData] = useState({
    countryCode: 'ht',
    address: "",
    city: "",
    region: "",
    zipCode: "",
    address_details: "",
    lat: null,
    lng: null
  });
  const [newAddressName, setNewAddressName] = useState("");
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    countryCode: 'ht',
    address: "",
    city: "",
    region: "",
    zipCode: "",
    address_details: "",
    lat: null,
    lng: null
  });

  const [formData, setFormData] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API_URL}/users/profile`, formData);
      await refreshUser();
      toast.success("Profil mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAddAddress = async () => {
    if (!newAddressData.address) return;
    setLoading(true);

    const coords = (newAddressData.lat && newAddressData.lng)
      ? { lat: newAddressData.lat, lon: newAddressData.lng }
      : await geocodeAddress(newAddressData.address);

    const currentAddresses = user?.saved_addresses || [];
    const label = newAddressName || (currentAddresses.length === 0 ? "Maison" : "Autre");

    const updatedAddresses = [...currentAddresses, {
      id: Date.now(),
      address: newAddressData.address,
      country_code: newAddressData.countryCode,
      city: newAddressData.city,
      region: newAddressData.region,
      zip_code: newAddressData.zipCode,
      address_details: newAddressData.address_details,
      name: label,
      coordinates: coords
    }];

    try {
      await axios.put(`${API_URL}/users/profile`, { saved_addresses: updatedAddresses });
      await refreshUser();
      setNewAddressData({
        countryCode: 'ht', address: "", city: "", region: "", zipCode: "", address_details: "", lat: null, lng: null
      });
      setNewAddressName("");
      toast.success("Adresse ajoutée");
    } catch (e) {
      toast.error("Erreur ajout adresse");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAddress = async (id) => {
    const currentAddresses = user?.saved_addresses || [];
    const updatedAddresses = currentAddresses.filter(a => a.id !== id);

    // If we removed the default address, clear the default_address_id
    const updatePayload = { saved_addresses: updatedAddresses };
    if (user?.default_address_id === id) {
      updatePayload.default_address_id = null;
    }

    try {
      await axios.put(`${API_URL}/users/profile`, updatePayload);
      await refreshUser();
      toast.success("Adresse supprimée");
    } catch (e) {
      toast.error("Erreur suppression adresse");
    }
  };

  const handleSetDefaultAddress = async (id) => {
    try {
      await axios.put(`${API_URL}/users/profile`, { default_address_id: id });
      await refreshUser();
      toast.success("Adresse par défaut mise à jour");
    } catch (e) {
      toast.error("Erreur mise à jour adresse par défaut");
    }
  };

  const handleEditAddress = (addr) => {
    setEditingAddress(addr);
    setEditForm({
      name: addr.name,
      countryCode: addr.country_code || 'ht',
      address: addr.address,
      city: addr.city || '',
      region: addr.region || '',
      zipCode: addr.zip_code || '',
      address_details: addr.address_details || '',
      lat: addr.coordinates?.lat || null,
      lng: addr.coordinates?.lon || addr.coordinates?.lng || null
    });
  };

  const handleUpdateAddress = async () => {
    if (!editForm.address) return;
    setLoading(true);
    const coords = (editForm.lat && editForm.lng)
      ? { lat: editForm.lat, lon: editForm.lng }
      : await geocodeAddress(editForm.address);

    const currentAddresses = user?.saved_addresses || [];
    const updatedAddresses = currentAddresses.map(a =>
      a.id === editingAddress.id
        ? {
          ...a,
          name: editForm.name,
          address: editForm.address,
          city: editForm.city,
          region: editForm.region,
          zip_code: editForm.zipCode,
          country_code: editForm.countryCode,
          address_details: editForm.address_details,
          coordinates: coords
        }
        : a
    );

    try {
      await axios.put(`${API_URL}/users/profile`, { saved_addresses: updatedAddresses });
      await refreshUser();
      setEditingAddress(null);
      toast.success("Adresse mise à jour");
    } catch (e) {
      toast.error("Erreur mise à jour adresse");
    } finally {
      setLoading(false);
    }
  };

  const verificationLabels = {
    basic: "Basique",
    business: "Business",
    verified: "Vérifié"
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl py-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
            Mon Profil
          </h1>
          <p className="text-muted-foreground">
            Gérez vos informations personnelles et vos préférences
          </p>
        </div>

        <div className="space-y-6">

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Informations personnelles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Prénom</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) =>
                        setFormData({ ...formData, first_name: e.target.value })
                      }
                      data-testid="profile-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Nom</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) =>
                        setFormData({ ...formData, last_name: e.target.value })
                      }
                      data-testid="profile-lastname"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{user?.email}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder="+509 ..."
                    data-testid="profile-phone"
                  />
                </div>

                <Button type="submit" disabled={loading} data-testid="save-profile">
                  {loading ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Manage Addresses Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Mes Adresses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="pt-2">
                {!showAddAddress ? (
                  <Button
                    variant="outline"
                    className="w-full border-dashed border-2 py-8 flex items-center justify-center gap-2 hover:bg-primary/5 hover:border-primary/50 transition-all group"
                    onClick={() => setShowAddAddress(true)}
                  >
                    <Plus className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
                    <span className="font-semibold text-primary">Ajouter une nouvelle adresse</span>
                  </Button>
                ) : (
                  <div className="space-y-4 bg-gray-50 p-4 rounded-xl border border-gray-100 animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="flex justify-between items-center mb-2">
                      <Label className="font-bold text-gray-700">Nouvelle adresse</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAddAddress(false)}
                        className="h-8 text-gray-400 hover:text-red-500 hover:bg-red-50"
                      >
                        <X className="h-4 w-4 mr-1" /> Terminer
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-gray-500">Nom de l'adresse</Label>
                      <Input
                        placeholder="Nom Complet, Maison, Bureau, etc."
                        value={newAddressName}
                        onChange={(e) => setNewAddressName(e.target.value)}
                        className="bg-white"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-gray-500">Localisation</Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <AddressFormGroup
                            addressData={newAddressData}
                            onChange={setNewAddressData}
                          />
                        </div>
                        <Button
                          onClick={async () => {
                            await handleAddAddress();
                            setShowAddAddress(false);
                          }}
                          className="h-11 w-11 shrink-0 px-0 shadow-sm"
                          disabled={loading}
                        >
                          <Plus className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-2">
                {user?.saved_addresses?.length > 0 ? (
                  user.saved_addresses.map((addr, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white border rounded-md">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-500">
                          <Home className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm">{addr.name || "Adresse"}</p>
                            {user?.default_address_id != null && user?.default_address_id === addr.id && (
                              <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold uppercase">
                                Défaut
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{addr.address}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {user?.default_address_id !== addr.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-muted-foreground h-8 px-2"
                            onClick={() => handleSetDefaultAddress(addr.id)}
                          >
                            Par défaut
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => handleEditAddress(addr)}
                        >
                          <Pencil className="h-4 w-4 text-gray-400" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => handleRemoveAddress(addr.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Aucune adresse enregistrée.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Account Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Compte
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Niveau de vérification</p>
                  <p className="text-sm text-muted-foreground">
                    {verificationLabels[user?.verification_tier] || "Basique"}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Portefeuille</p>
                  <p className="text-2xl font-bold text-primary">
                    {(user?.wallet_balance || 0).toFixed(2)} HTG
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <p className="font-medium mb-2">Code de parrainage</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted px-4 py-2 rounded font-mono text-lg">
                    {user?.referral_code || "---"}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyReferralCode}
                    data-testid="copy-referral-profile"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Partagez ce code pour gagner des commissions sur les achats de
                  vos filleuls.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Active Roles Card */}
          <Card>
            <CardHeader>
              <CardTitle>Rôles actifs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {user?.roles?.map((role, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium capitalize"
                  >
                    {role.role_type}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />

      {/* Edit Address Dialog */}
      <Dialog open={!!editingAddress} onOpenChange={(open) => !open && setEditingAddress(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'adresse</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Nom (ex: Nom Complet, Maison, Bureau)</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Adresse complète</Label>
              <AddressFormGroup
                addressData={editForm}
                onChange={(newData) => setEditForm(prev => ({ ...prev, ...newData }))}
              />
            </div>
            <Button
              className="w-full"
              onClick={handleUpdateAddress}
              disabled={loading}
            >
              {loading ? "Mise à jour..." : "Enregistrer les modifications"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
